import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Activity, Shield, Calendar, DollarSign, FileText, Users, ChevronRight, History, QrCode, FileSignature, Package, ArrowLeft, Loader2, Mail } from 'lucide-react';

export const LandingPage = () => {
  const { user, login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '' });
  
  // Forgot password state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  
  // Reset password state (when user clicks link from email)
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  // Check for reset token in URL - run only once on mount
  useEffect(() => {
    const token = searchParams.get('reset_token');
    if (token && !resetToken) {
      console.log('Found reset token in URL:', token.substring(0, 10) + '...');
      setResetToken(token);
      setResetPasswordOpen(true);
      // Clear the token from URL without triggering re-render loop
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('reset_token');
      window.history.replaceState({}, '', `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`);
    }
  }, []); // Empty dependency array - run only once on mount

  // Redirect if already logged in
  if (user) {
    navigate('/dashboard');
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const result = await login(loginData.email, loginData.password);
      if (result && result.success) {
        toast.success('Welcome back!');
        navigate('/dashboard');
      } else {
        // Login failed - show error
        const errorMessage = result?.error || 'Login failed. Please check your credentials.';
        toast.error(errorMessage);
      }
    } catch (error) {
      // Safely extract error message to avoid cloning issues
      let errorMessage = 'Login failed. Please check your credentials.';
      try {
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error && typeof error.message === 'string') {
          errorMessage = error.message;
        }
      } catch (e) {
        // Fallback if error object can't be read
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await register(registerData.name, registerData.email, registerData.password);
      if (result && result.token) {
        toast.success('Account created successfully!');
        navigate('/dashboard');
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } catch (error) {
      // Safely extract error message to avoid cloning issues
      console.log('Registration error:', error);
      let errorMessage = 'Registration failed. Please try again.';
      try {
        if (error && typeof error === 'object' && 'message' in error) {
          errorMessage = String(error.message);
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
      } catch (e) {
        // Ignore - use default message
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    
    setIsSendingReset(true);
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      
      if (response.ok) {
        setResetSent(true);
        toast.success('Check your email for reset instructions');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send reset email');
      }
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (!resetToken) {
      toast.error('Reset token is missing. Please use the link from your email again.');
      return;
    }
    
    setIsResetting(true);
    try {
      console.log('Attempting password reset with token:', resetToken.substring(0, 10) + '...');
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: newPassword })
      });
      
      const data = await response.json();
      console.log('Reset response:', response.status, data);
      
      if (response.ok) {
        toast.success('Password reset successfully! You can now log in.');
        setResetPasswordOpen(false);
        setResetToken('');
        setNewPassword('');
        setConfirmNewPassword('');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  const features = [
    { icon: History, title: 'Horse Identification & History', desc: "Your horse's lifetime in one place, all tracked and transferable to a new owner" },
    { icon: QrCode, title: 'QR Code ID', desc: 'Unique QR Code for your horse linked to contact details and emergency information, download and print to a bridle/halter tag' },
    { icon: FileSignature, title: 'Contracts', desc: 'Pre-loaded with Sale, Trial & Lease contract templates' },
    { icon: Activity, title: 'Health Tracking', desc: 'Vet visits, farrier, worming, records, document storage and reminders' },
    { icon: Calendar, title: 'Event & Ride Logs', desc: 'Track show attendance, results, lessons & riding notes' },
    { icon: DollarSign, title: 'Expense Tracking', desc: 'Monitor costs for each horse, generate reports' },
    { icon: FileText, title: 'Document Vault', desc: 'Store registrations, vet reports and more' },
    { icon: Package, title: 'Tack Inventory', desc: 'Track saddles, bridles and equipment with receipt storage, great for warranty & insurance purposes' },
    { icon: Users, title: 'Share Access', desc: 'Collaborate with staff or family members' },
  ];

  return (
    <div className="min-h-screen bg-bone">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Image - Desktop */}
        <div className="hidden lg:block absolute inset-0 pointer-events-none">
          <div className="absolute right-0 top-0 w-1/2 h-full">
            <img 
              src="https://images.unsplash.com/photo-1570908729019-931a78c8e9a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" 
              alt="Horse portrait"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-bone via-bone/80 to-transparent" />
          </div>
        </div>
        
        <div className="absolute inset-0 bg-gradient-to-br from-forest/5 to-saddle/5" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Hero Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-forest/10 text-forest text-sm font-medium">
                  <span className="w-2 h-2 rounded-full bg-forest animate-pulse" />
                  Premium Horse Management
                </div>
                <div className="flex items-center justify-center gap-3">
                  <img src="/logo.png" alt="EquiVault" className="h-16 w-auto" />
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground font-serif">
                    EquiVault
                  </h1>
                </div>
                <p className="text-base sm:text-lg text-saddle font-medium italic">
                  "Because every horse deserves a history"
                </p>
                <p className="text-lg text-muted-foreground max-w-md">
                  The complete database for managing your horses. Track health, training, events, expenses, and documents all in one beautiful place.
                </p>
              </div>

              {/* Feature Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {features.map((feature, i) => (
                  <div 
                    key={feature.title}
                    className="flex flex-col gap-2 p-3 rounded-xl bg-white shadow-card animate-slideUp"
                    style={{ animationDelay: `${i * 0.05}s` }}
                  >
                    <feature.icon className="w-5 h-5 text-saddle" />
                    <span className="font-medium text-sm">{feature.title}</span>
                    <span className="text-xs text-muted-foreground leading-relaxed">{feature.desc}</span>
                  </div>
                ))}
              </div>

              {/* Hero Image - Mobile */}
              <div className="lg:hidden">
                <img 
                  src="https://images.unsplash.com/photo-1570908729019-931a78c8e9a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                  alt="Horse portrait"
                  className="w-full h-64 object-cover rounded-2xl shadow-card-hover"
                />
              </div>
            </div>

            {/* Right - Auth Card */}
            <div className="w-full max-w-md mx-auto lg:mx-0">
              <Card className="shadow-card-hover border-0 bg-white/80 backdrop-blur-sm" data-testid="auth-card">
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-2xl font-serif">Get Started</CardTitle>
                  <CardDescription>Sign in or create your account</CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="login" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-6 bg-muted">
                      <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                      <TabsTrigger value="register" data-testid="register-tab">Sign Up</TabsTrigger>
                    </TabsList>

                    <TabsContent value="login" className="space-y-4">
                      <form onSubmit={handleLogin} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="login-email">Email</Label>
                          <Input
                            id="login-email"
                            type="email"
                            placeholder="you@example.com"
                            value={loginData.email}
                            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                            required
                            data-testid="login-email-input"
                            className="bg-white/50"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="login-password">Password</Label>
                          <Input
                            id="login-password"
                            type="password"
                            placeholder="••••••••"
                            value={loginData.password}
                            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                            required
                            data-testid="login-password-input"
                            className="bg-white/50"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full bg-forest hover:bg-forest-dark text-white"
                          disabled={isLoading}
                          data-testid="login-submit-btn"
                        >
                          {isLoading ? 'Signing in...' : 'Sign In'}
                        </Button>
                        <Button
                          type="button"
                          variant="link"
                          className="w-full text-sm text-muted-foreground hover:text-forest"
                          onClick={() => {
                            setForgotEmail(loginData.email);
                            setForgotPasswordOpen(true);
                          }}
                          data-testid="forgot-password-btn"
                        >
                          Forgot your password?
                        </Button>
                      </form>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-muted" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-white px-2 text-muted-foreground">Or continue with</span>
                        </div>
                      </div>

                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={loginWithGoogle}
                        data-testid="google-login-btn"
                      >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                      </Button>
                    </TabsContent>

                    <TabsContent value="register" className="space-y-4">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center mb-4">
                        <p className="text-amber-800 font-medium text-sm">🎉 Start with 14 days free trial</p>
                        <p className="text-amber-600 text-xs">Full access to all features, no credit card required</p>
                      </div>
                      <form onSubmit={handleRegister} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="register-name">Full Name</Label>
                          <Input
                            id="register-name"
                            type="text"
                            placeholder="John Smith"
                            value={registerData.name}
                            onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                            required
                            data-testid="register-name-input"
                            className="bg-white/50"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="register-email">Email</Label>
                          <Input
                            id="register-email"
                            type="email"
                            placeholder="you@example.com"
                            value={registerData.email}
                            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                            required
                            data-testid="register-email-input"
                            className="bg-white/50"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="register-password">Password</Label>
                          <Input
                            id="register-password"
                            type="password"
                            placeholder="••••••••"
                            value={registerData.password}
                            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                            required
                            minLength={6}
                            data-testid="register-password-input"
                            className="bg-white/50"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full bg-forest hover:bg-forest-dark text-white"
                          disabled={isLoading}
                          data-testid="register-submit-btn"
                        >
                          {isLoading ? 'Creating account...' : 'Create Account'}
                        </Button>
                      </form>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-muted" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-white px-2 text-muted-foreground">Or continue with</span>
                        </div>
                      </div>

                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={loginWithGoogle}
                        data-testid="google-register-btn"
                      >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                      </Button>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-forest" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Enter your email and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          
          {resetSent ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-forest/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-forest" />
              </div>
              <h3 className="font-semibold mb-2">Check your email</h3>
              <p className="text-muted-foreground text-sm mb-4">
                We've sent a password reset link to <strong>{forgotEmail}</strong>
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setForgotPasswordOpen(false);
                  setResetSent(false);
                  setForgotEmail('');
                }}
              >
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email Address</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="you@example.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setForgotPasswordOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-forest hover:bg-forest-dark"
                  disabled={isSendingReset}
                >
                  {isSendingReset ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog (opened via email link) */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set New Password</DialogTitle>
            <DialogDescription>
              Enter your new password below.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="••••••••"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-forest hover:bg-forest-dark"
              disabled={isResetting}
            >
              {isResetting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting...</>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
